import { apiSchemas } from '@verifime/api-definition';
import {
  type TEntityChoice,
  type TEntityPlanRow,
  type TSelectableEntityType,
} from '@verifime/components';
import { EntityStatus, IDENTITY_TYPES_MAP, IdentityTypeCode } from '@verifime/utils';
import { z } from 'zod';
import {
  buildAction,
  NOTHING_TO_START_LABEL,
  primaryLabelFor,
  targetFor,
  type TConfirmAction,
} from './entityPlanAction';
import {
  individualRow,
  latestIndividual,
  personOf,
  pickEntity,
  type TEntityPick,
  type TPlanChosen,
  type TPlanPerson,
} from './entityPlanIdentities';

export { NOTHING_TO_START_LABEL, type TConfirmAction, type TPlanChosen, type TPlanPerson };

/** `PermissionRequestSummary.requestType`: Legacy, Individual, or one of the seven entity types. */
export type TPermissionRequestType = z.infer<typeof apiSchemas.PermissionRequestType>;

/** The seven entity request types: every request type but Legacy and Individual. */
export type TEntityRequestType = Exclude<TPermissionRequestType, 'Legacy' | 'Individual'>;

/**
 * What the confirm screen's primary action leads to: the individual's wizard, the chosen entity's,
 * or nothing left to start (the individual is already submitted and no entity is chosen). The
 * caller decides where each goes.
 */
export type TEntityPlanTarget = 'individual' | TSelectableEntityType | 'nothing-to-start';

/** The fields of a `PermissionRequestSummary` the plan reads. */
export type TPlanRequest = {
  id?: string;
  organisationId?: string;
  /** Legacy (the original flow, never part of a plan), Individual, or an entity type. */
  requestType?: TPermissionRequestType;
  /** Absent while the request is open. */
  completedDateTime?: string;
  /** Set when the request was closed by a grant; a discarded request has none. */
  permissionId?: string;
};

/** The fields of a customer identity (`GET /v1/customer/me`) the plan reads. */
export type TPlanIdentity = {
  id?: string;
  entityType?: string;
  entityStatus?: string;
  customerName?: string;
  createdDate?: string;
};

export type TEntityPlan = {
  /** The choice the plan is for: what the confirmation sends. */
  choice: TEntityChoice;
  rows: TEntityPlanRow[];
  primaryLabel: string;
  /**
   * The wizard to start next, or nothing to start. A share, or nothing left, both read as nothing
   * to start. `action` says more; this stays for the callers that only route.
   */
  target: TEntityPlanTarget;
  /** What the primary grants to the client, then where the customer goes. */
  action: TConfirmAction;
  /** The Individual the confirmation is about. */
  person: TPlanPerson;
  showChange: boolean;
  /**
   * True when the client's open request already matches the plan's choice, so the primary action
   * has nothing to confirm and goes straight on. False when no request is open or the choice
   * differs from the open one. A granted (closed) request never confirms: it is not this invite's
   * confirmation (#688 Q1).
   */
  isConfirmed: boolean;
};

/** The entity request types, derived from the generated `PermissionRequestType` enum. */
const ENTITY_REQUEST_TYPES: readonly TPermissionRequestType[] =
  apiSchemas.PermissionRequestType.options.filter(
    (requestType) => requestType !== 'Legacy' && requestType !== 'Individual',
  );

export const isEntityRequestType = (requestType: unknown): requestType is TEntityRequestType =>
  ENTITY_REQUEST_TYPES.includes(requestType as TPermissionRequestType);

const warnedUnmappedEntityTypes = new Set<string>();

/**
 * The entity label from the existing identity type labels (for example "Sole Trader"). An entity
 * type with no label there (none of the seven selectable types) shows its raw value rather than an
 * authored label, with one warning per type so the gap is visible without flooding the console.
 */
export const entityTypeLabel = (entityType: string): string => {
  const label = IDENTITY_TYPES_MAP[entityType as IdentityTypeCode]?.label;
  if (label) {
    return label;
  }
  if (!warnedUnmappedEntityTypes.has(entityType)) {
    warnedUnmappedEntityTypes.add(entityType);
    console.warn('Entity plan: no label for entity type, showing the raw value', entityType);
  }
  return entityType;
};

const isOpen = (request: TPlanRequest) => !request.completedDateTime;

const isGranted = (request: TPlanRequest) => !isOpen(request) && !!request.permissionId;

/** Completion time in epoch ms; an absent or unparseable date counts as the earliest. */
const completedAt = (request: TPlanRequest) => {
  const time = Date.parse(request.completedDateTime ?? '');
  return Number.isNaN(time) ? Number.NEGATIVE_INFINITY : time;
};

const isIndividualRequest = (request: TPlanRequest) => request.requestType === 'Individual';

const isEntityRequest = (request: TPlanRequest) => isEntityRequestType(request.requestType);

/** An ask-first request. Legacy (and anything unrecognised) never is. */
const isAskFirstRequest = (request: TPlanRequest) =>
  isIndividualRequest(request) || isEntityRequest(request);

/**
 * The type of the open entity request the plan is for: the one named (the request being worked
 * on, when a late invite leaves two open: S26, S27), else the first open one.
 */
function openEntityTypeOf(
  clientRequests: TPlanRequest[],
  named: TEntityRequestType | undefined,
): TEntityRequestType | undefined {
  const openTypes = clientRequests
    .filter(isOpen)
    .map((request) => request.requestType)
    .filter(isEntityRequestType);
  return openTypes.find((requestType) => requestType === named) ?? openTypes[0];
}

/** The most recently granted entity request's type: a client can grant one entity, then another. */
function latestGrantedEntityTypeOf(clientRequests: TPlanRequest[]): TEntityRequestType | undefined {
  const latest = clientRequests
    .filter((request) => isGranted(request) && isEntityRequest(request))
    .reduce<
      TPlanRequest | undefined
    >((best, next) => (!best || completedAt(next) > completedAt(best) ? next : best), undefined);
  return isEntityRequestType(latest?.requestType) ? latest.requestType : undefined;
}

/**
 * True once the customer has an entity draft of the open entity request's type (#616, #544 Q4):
 * Continue on page 1 of that entity's wizard created it, and the choice is committed. Only a
 * Draft counts: a submitted or verified identity of the type, or a draft of another type, does not.
 */
export const hasEntityDraftFor = (
  openEntityType: TEntityRequestType | undefined,
  identities: readonly TPlanIdentity[],
): boolean =>
  !!openEntityType &&
  identities.some(
    (identity) =>
      identity.entityType === openEntityType && identity.entityStatus === EntityStatus.Draft,
  );

/** What the client's ask-first requests show: the choice they hold and whether it may change. */
type TRequestStanding = {
  /**
   * The choice the requests show, open or closed: the open entity request, else the latest granted
   * one, else just the individual. It is the plan's choice when the caller gives none and no
   * request is open, so the plan stays loaded once the last open request closes. It does not say
   * what is confirmed: see `openChoice`.
   */
  requestsChoice: TEntityChoice;
  /**
   * The choice an open request holds: the open entity type, else just me when an Individual
   * request is open, else undefined. Only an open request is this invite's confirmation (#688 Q1);
   * a granted one never is.
   */
  openChoice: TEntityChoice | undefined;
  isChangeOffered: boolean;
};

/** The choice the client's open requests hold, or undefined when none is open. */
function openChoiceOf(
  clientRequests: TPlanRequest[],
  openEntityType: TEntityRequestType | undefined,
): TEntityChoice | undefined {
  if (openEntityType) {
    return openEntityType;
  }
  return clientRequests.some((request) => isIndividualRequest(request) && isOpen(request))
    ? 'individual'
    : undefined;
}

function standingOf(
  clientRequests: TPlanRequest[],
  entityRequestType: TEntityRequestType | undefined,
  entityDraftExists: (openEntityType: TEntityRequestType | undefined) => boolean,
): TRequestStanding {
  const openEntityType = openEntityTypeOf(clientRequests, entityRequestType);
  // A discarded request (closed, no permission) no longer counts.
  const latestGrantedEntityType = latestGrantedEntityTypeOf(clientRequests);
  return {
    requestsChoice: openEntityType ?? latestGrantedEntityType ?? 'individual',
    openChoice: openChoiceOf(clientRequests, openEntityType),
    // A granted request does not lock the choice (#688 Q1): a re-invite may choose again, and the
    // server refuses what it must (#544 Q1). On the request path the customer works on one request:
    // there is no choice to change. Once the entity draft exists the choice is committed (#616).
    isChangeOffered: !entityRequestType && !entityDraftExists(openEntityType),
  };
}

type TPickContext = Pick<
  TBuildEntityPlanInput,
  'chosen' | 'identities' | 'declaredOrganisationName' | 'sharedIdentityIds'
>;

/** What the plan does with the entity the choice names; nothing when the choice is just me. */
const pickFor = (choice: TEntityChoice, context: TPickContext): TEntityPick | undefined =>
  choice === 'individual'
    ? undefined
    : pickEntity({ ...context, entityType: choice, entityLabel: entityTypeLabel(choice) });

/** The Individual's row, then the chosen entity's. */
const rowsOf = (
  individual: TPlanIdentity | undefined,
  pick: TEntityPick | undefined,
  sharedIdentityIds: ReadonlySet<string> | undefined,
) => {
  const personRow = individualRow(individual, sharedIdentityIds);
  return pick ? [personRow, pick.row] : [personRow];
};

export type TBuildEntityPlanInput = {
  organisationId: string;
  /** The choice to plan for; without it, the choice the client's requests show. */
  choice?: TEntityChoice;
  /**
   * What the customer chose on the choose screen. `{ identityId }` is a wallet identity; `{}` is a
   * new entity. Absent when no choice was carried (a resumed screen): the row is then a new entity
   * too, as Continue starts one, and nothing is shared for the entity.
   */
  chosen?: TPlanChosen;
  /**
   * The open entity request being worked on, when the plan is opened from a request (the request
   * path). With two entity requests open it picks one; without it the first open one is used.
   */
  entityRequestType?: TEntityRequestType;
  requests: TPlanRequest[];
  identities: TPlanIdentity[];
  /**
   * The ids of the customer's identities already shared with this client. Absent when that was not
   * read: then nothing is shared.
   */
  sharedIdentityIds?: ReadonlySet<string>;
  /** The organisation name the partner declared for a new entity of this type, as given. */
  declaredOrganisationName?: string;
  /**
   * Closes the change once a Draft of the open entity request's type exists (#616). Off by default:
   * the customer portal always turns it on (#689, no toggle), and with it off the change link stays
   * whenever the plan is not on the request path. A granted request never hides it (#688 Q1).
   */
  closeChangeOnEntityDraft?: boolean;
};

/**
 * Builds the confirm screen's plan for one client: the individual's row, then the chosen entity's
 * row (if any), what the primary action grants and where it leads, and whether the choice may
 * still change.
 *
 * - The plan is for `choice` when the caller gives one (a choice about to be confirmed, before any
 *   request exists or replacing the open one). Without it, the plan is for the client's open
 *   request (an entity, else just the individual); with none open, for the latest granted entity,
 *   else just the individual, so a plan is still shown when the last open request has closed.
 * - Only the client's ask-first requests (`requestType` Individual or an entity type) count. A
 *   Legacy request is never the individual's request. With no choice and no such request there is
 *   no plan (`null`), never an assumed individual-only plan.
 * - A submitted individual is never started again, and is shared as it is when not yet shared
 *   with this client (#609 Q7). A chosen wallet entity is shared; a chosen Draft is resumed.
 * - The choice may change, whatever the client's requests show: a granted request does not lock it
 *   (#688 Q1), and the server refuses what it must. On the request path it does not change at all.
 *   With `closeChangeOnEntityDraft`, it also closes once a Draft of the open entity request's type
 *   exists (#616).
 * - The plan is confirmed when the client's open request already holds its choice. A granted
 *   request never confirms, so a re-invite for a held type is confirmed again (#688 Q1).
 */
export function buildEntityPlan({
  organisationId,
  choice,
  chosen,
  entityRequestType,
  requests,
  identities,
  sharedIdentityIds,
  declaredOrganisationName,
  closeChangeOnEntityDraft,
}: TBuildEntityPlanInput): TEntityPlan | null {
  const clientRequests = requests.filter(
    (request) => request.organisationId === organisationId && isAskFirstRequest(request),
  );
  if (!choice && clientRequests.length === 0) {
    return null;
  }
  const entityDraftExists = (openEntityType: TEntityRequestType | undefined) =>
    !!closeChangeOnEntityDraft && hasEntityDraftFor(openEntityType, identities);
  const standing = standingOf(clientRequests, entityRequestType, entityDraftExists);
  const planChoice: TEntityChoice = choice ?? standing.openChoice ?? standing.requestsChoice;
  const individual = latestIndividual(identities);
  const pick = pickFor(planChoice, {
    chosen,
    identities,
    declaredOrganisationName,
    sharedIdentityIds,
  });
  const action = buildAction({ individual, pick, sharedIdentityIds });

  return {
    choice: planChoice,
    rows: rowsOf(individual, pick, sharedIdentityIds),
    primaryLabel: primaryLabelFor(action.then),
    target: targetFor(action.then),
    action,
    person: personOf(individual),
    showChange: standing.isChangeOffered,
    isConfirmed: standing.openChoice !== undefined && planChoice === standing.openChoice,
  };
}
